import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Card } from 'react-bootstrap';
import { validateEventData, sanitizeEventData, eventFields } from '../utils/eventValidation';

const EMPTY_EVENT = {
    slug: '',
    name: '',
    backendId: '',
    shortDescription: '',
    description: '',
    rules: [],
    submissiondate: '',
    prize: '',
    team: false,
    min: '',
    max: '',
    registrationStarted: false,
    psLink: '',
    elink: '',
    contact: [{ name: '', number: '' }],
};

const EventForm = ({ eventData = null, onSubmit, onCancel, isEditing = false }) => {
    const [formData, setFormData] = useState(EMPTY_EVENT);
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rulesText, setRulesText] = useState('');

    useEffect(() => {
        if (eventData) {
            const initialData = {
                ...EMPTY_EVENT,
                ...eventData,
                contact:
                    Array.isArray(eventData.contact) && eventData.contact.length
                        ? eventData.contact.map((c) => ({ name: c?.name || '', number: c?.number || '' }))
                        : [{ name: '', number: '' }],
            };
            if (Array.isArray(initialData.rules)) {
                setRulesText(initialData.rules.join('\n'));
            }
            setFormData(initialData);
        }
    }, [eventData]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleRulesChange = (e) => {
        const text = e.target.value;
        setRulesText(text);
        const rulesArray = text.split('\n').filter((rule) => rule.trim() !== '');
        setFormData((prev) => ({ ...prev, rules: rulesArray }));
    };

    const setContactField = (idx, key, value) => {
        setFormData((prev) => {
            const contact = prev.contact.map((c, i) => (i === idx ? { ...c, [key]: value } : c));
            return { ...prev, contact };
        });
    };

    const addContact = () => {
        setFormData((prev) => ({ ...prev, contact: [...prev.contact, { name: '', number: '' }] }));
    };

    const removeContact = (idx) => {
        setFormData((prev) => ({
            ...prev,
            contact: prev.contact.filter((_, i) => i !== idx),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors([]);

        const sanitized = sanitizeEventData({
            ...formData,
            rules: rulesText.split('\n').filter((rule) => rule.trim() !== ''),
        });

        const validation = validateEventData(sanitized);
        if (!validation.isValid) {
            setErrors(validation.errors);
            setIsSubmitting(false);
            return;
        }

        try {
            await onSubmit(sanitized);
            if (!isEditing) {
                setFormData(EMPTY_EVENT);
                setRulesText('');
            }
        } catch (error) {
            setErrors([error.message || 'Failed to save event']);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderField = (field) => {
        const { name, label, type, required, placeholder, helpText, dependsOn, custom } = field;

        if (dependsOn && !formData[dependsOn]) return null;

        if (custom && name === 'rules') {
            return (
                <Form.Group key={name} className="mb-3">
                    <Form.Label>{label} {required && <span className="text-danger">*</span>}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={5}
                        value={rulesText}
                        onChange={handleRulesChange}
                        placeholder={placeholder}
                        required={required}
                    />
                    {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
                </Form.Group>
            );
        }

        if (custom && name === 'contact') {
            return (
                <Form.Group key={name} className="mb-3">
                    <Form.Label>{label}</Form.Label>
                    {formData.contact.map((c, i) => (
                        <Row key={i} className="mb-2 g-2 align-items-center">
                            <Col md={5}>
                                <Form.Control
                                    type="text"
                                    placeholder="Name"
                                    value={c.name || ''}
                                    onChange={(e) => setContactField(i, 'name', e.target.value)}
                                />
                            </Col>
                            <Col md={5}>
                                <Form.Control
                                    type="text"
                                    placeholder="Number"
                                    value={c.number || ''}
                                    onChange={(e) => setContactField(i, 'number', e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeContact(i)}
                                    disabled={formData.contact.length <= 1}
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    ))}
                    <Button variant="outline-secondary" size="sm" onClick={addContact}>
                        + Add contact
                    </Button>
                    {helpText && <Form.Text className="text-muted d-block mt-1">{helpText}</Form.Text>}
                </Form.Group>
            );
        }

        switch (type) {
            case 'textarea':
                return (
                    <Form.Group key={name} className="mb-3">
                        <Form.Label>{label} {required && <span className="text-danger">*</span>}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name={name}
                            value={formData[name] || ''}
                            onChange={handleInputChange}
                            placeholder={placeholder}
                            required={required}
                        />
                        {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
                    </Form.Group>
                );

            case 'checkbox':
                return (
                    <Form.Group key={name} className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id={name}
                            name={name}
                            label={label}
                            checked={formData[name] || false}
                            onChange={handleInputChange}
                        />
                        {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
                    </Form.Group>
                );

            default:
                return (
                    <Form.Group key={name} className="mb-3">
                        <Form.Label>{label} {required && <span className="text-danger">*</span>}</Form.Label>
                        <Form.Control
                            type={type}
                            name={name}
                            value={formData[name] ?? ''}
                            onChange={handleInputChange}
                            placeholder={placeholder}
                            required={required}
                        />
                        {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
                    </Form.Group>
                );
        }
    };

    return (
        <Card>
            <Card.Header>
                <h4>{isEditing ? 'Edit Event' : 'Add New Event'}</h4>
            </Card.Header>
            <Card.Body>
                {errors.length > 0 && (
                    <Alert variant="danger">
                        <ul className="mb-0">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            {eventFields.slice(0, Math.ceil(eventFields.length / 2)).map(renderField)}
                        </Col>
                        <Col md={6}>
                            {eventFields.slice(Math.ceil(eventFields.length / 2)).map(renderField)}
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2">
                        {onCancel && (
                            <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
                                Cancel
                            </Button>
                        )}
                        <Button variant="primary" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : (isEditing ? 'Update Event' : 'Add Event')}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default EventForm;
